import { useCallback, useMemo, useRef, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';
import { array, InferType, number, object, ObjectSchema, string } from 'yup';

import {
  AddressValue,
  // AreaValue, // COMMENTED OUT: Matrikkeldetaljer
  AssetClass,
  BuildingReadModel,
  BuildingType,
  // CadastralValue, // COMMENTED OUT: Matrikkeldetaljer
  EnergyLabel,
  SetBuildingPackagePayload,
  SetBuildingPayload,
  // SpaceSourceExternalReferenceValue, // COMMENTED OUT: Matrikkeldetaljer
  StandardType,
} from '@client/api';
import {
  addressValueSchema,
  AreaAdornment,
  Button,
  cadastralValueSchema,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  Skeleton,
  spaceSourceExternalReferenceValueSchema,
  TagAutocomplete,
  transformToOptionalNumber,
  Typography,
  YupRemoveIndex,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { BuildingAddressForm } from './building-address.form';
import { BuildingPackagesSection } from './building-packages-section';
// COMMENTED OUT: Matrikkeldetaljer - may be used in the future when NorkartSpace API is enabled
// import { BuildingCadastralInformationForm } from './building-cadastral-information.form';

import { CloseRequestModal } from '../../../../../components/close-request-modal/close-request-modal.component';

const BUILDING_TYPE_OPTIONS: BuildingType[] = [
  'Office',
  'RetailHighStreet',
  'Retail',
  'Logistics',
  'Industrial',
  'Hotel',
  'Healthcare',
  'Education',
  'Mixed',
];

const STANDARD_TYPE_OPTIONS: StandardType[] = ['Low', 'Medium', 'High'];
const ENERGY_LABEL_OPTIONS: EnergyLabel[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

type FormModel = Omit<SetBuildingPayload, 'packages'>;

const schema: ObjectSchema<FormModel> = object({
  name: string().default(''), // with default we can have a "required" field which can be an empty string (api accepts empty string for name an auto generates it)
  description: string().nullable(),
  address: addressValueSchema.nullable().makeOptional(),
  areaMetric: number().min(0).transform(transformToOptionalNumber),
  source: spaceSourceExternalReferenceValueSchema.nullable().makeOptional(),
  standard: string<StandardType>().nullable().default(undefined),
  type: string<BuildingType>().nullable().default(undefined),
  energyLabel: string<EnergyLabel>().nullable().default(undefined),
  heatingGrade: string().nullable().default(undefined),
  // COMMENTED OUT: Matrikkeldetaljer - keeping schema field for API compatibility, but not used in UI
  cadastralInformation: cadastralValueSchema.nullable().makeOptional(),
  // Byggnr is immutable and not edited here; kept in the schema for payload type compatibility
  // and left undefined so the API preserves the existing value on update.
  buildingNumber: string().nullable().default(undefined),
  operationalCostPriceId: string().nullable().default(undefined),
  tags: array().of(string().required()).nullable().default(undefined),
  assetClass: string<AssetClass>().nullable().default(undefined),
});

type FormValues = YupRemoveIndex<InferType<typeof schema>>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type BuildingFormData = FormValues & { packages?: SetBuildingPackagePayload[] | null };

interface BuildingFormProps {
  title: React.ReactNode;
  onSubmit: (data: BuildingFormData) => Promise<void>;
  onClose: () => void;
  open?: boolean;
  isFetching?: boolean;
  building?: BuildingReadModel;
  hidePackages?: boolean;
  /**
   * Shows optional manual cadastral inputs (kommunenr/gnr/bnr/byggnr). Used by the manual
   * building registration in the wizard, where no search source can supply matrikkel data.
   */
  showCadastralFields?: boolean;
}

export function BuildingForm({
  open = true,
  title,
  onSubmit,
  onClose,
  isFetching = false,
  building,
  hidePackages = false,
  showCadastralFields = false,
}: BuildingFormProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: building?.name ?? '',
      description: building?.description ?? '',
      address: building?.address ?? undefined,
      areaMetric: building?.area?.metric ?? ('' as any),
      source: building?.source ?? undefined,
      standard: building?.standard ?? undefined,
      type: building?.type ?? undefined,
      energyLabel: building?.energyLabel ?? undefined,
      heatingGrade: building?.heatingGrade ?? undefined,
      // COMMENTED OUT: Matrikkeldetaljer - may be used in the future when NorkartSpace API is enabled
      // cadastralInformation: building?.cadastralInformation ?? undefined,
      operationalCostPriceId: building?.operationalCost?.id ?? undefined,
      tags: building?.tags ?? undefined,
    }),
    [
      building?.address,
      building?.area?.metric,
      // building?.cadastralInformation, // COMMENTED OUT: Matrikkeldetaljer
      building?.description,
      building?.energyLabel,
      building?.heatingGrade,
      building?.name,
      building?.operationalCost?.id,
      building?.source,
      building?.standard,
      building?.type,
      building?.tags,
    ],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty },
  } = methods;

  const [closingRequest, setClosingRequest] = useState<boolean>(false);

  const defaultPackages: SetBuildingPackagePayload[] = useMemo(() => {
    if (building?.packages && building.packages.length > 0) {
      return building.packages
        .filter((p) => p.isActive)
        .map((p) => ({ packageType: p.packageType, tier: p.tier ?? 'Basic' }));
    }
    return [];
  }, [building?.packages]);

  const [packages, setPackages] = useState<SetBuildingPackagePayload[]>(defaultPackages);

  const packagesChanged = useMemo(() => {
    if (packages.length !== defaultPackages.length) return true;
    return packages.some(
      (p, i) => p.packageType !== defaultPackages[i]?.packageType || p.tier !== defaultPackages[i]?.tier,
    );
  }, [packages, defaultPackages]);

  const selectedAddress = watch('address');
  // const selectedSource = watch('source'); // COMMENTED OUT: Matrikkeldetaljer
  const selectedTags = watch('tags');

  const [editingAddress, setEditingAddress] = useState(!selectedAddress);
  const handleToggleEditAddress = useCallback(() => setEditingAddress((current) => !current), []);

  const resetEditingState = useCallback(() => {
    setEditingAddress(!selectedAddress);
  }, [selectedAddress]);

  const checkCloseRequest = useCallback(() => {
    if (isDirty) {
      setClosingRequest(true);
    } else {
      resetEditingState();
      onClose();
    }
  }, [isDirty, onClose, resetEditingState]);
  const handleCancelCloseRequest = useCallback(() => setClosingRequest(false), []);
  const handleConfirmCloseRequest = useCallback(() => {
    resetEditingState();
    onClose();
    handleCancelCloseRequest();
  }, [onClose, handleCancelCloseRequest, resetEditingState]);

  /**
   * Matrikkel identifier the cadastral fields were last prefilled from. BuildingAddressForm
   * fires onChange on every keystroke and on country changes, all carrying the same address
   * source, so without this guard each prefill would overwrite the user's own corrections.
   */
  const prefilledMatrikkelRef = useRef<string | null>(null);

  const setAddressToFormValues = useCallback(
    (address: AddressValue | undefined = undefined) => {
      setValue('address', address, { shouldTouch: true, shouldValidate: true, shouldDirty: true });

      if (!showCadastralFields) return;

      // Kartverket-backed addresses carry the matrikkel as "kommunenr-gnr/bnr" in the source
      // identifier — prefill the manual cadastral fields (PlacePoint slot convention:
      // cadastralUnit=kommunenr, propertyUnit=gnr, leaseholdUnit=bnr) so the user only has to
      // type them for addresses the lookup couldn't resolve. Only on a newly picked address.
      const identifier =
        address?.source?.integration === 'Kartverket' ? (address.source.identifier ?? null) : null;

      if (identifier === prefilledMatrikkelRef.current) return;
      prefilledMatrikkelRef.current = identifier;

      const matrikkel = identifier ? /^(.+)-(.+)\/(.+)$/.exec(identifier) : null;
      if (matrikkel) {
        setValue(
          'cadastralInformation',
          { cadastralUnit: matrikkel[1], propertyUnit: matrikkel[2], leaseholdUnit: matrikkel[3] },
          { shouldTouch: true, shouldValidate: true, shouldDirty: true },
        );
      }
    },
    [setValue, showCadastralFields],
  );

  const setTagsToFormValues = useCallback(
    (e: CustomEvent<Tagify.ChangeEventData>) => {
      const tags = e.detail.tagify.getCleanValue().map((tag) => tag.value);
      setValue('tags', tags, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  // COMMENTED OUT: Matrikkeldetaljer - may be used in the future when NorkartSpace API is enabled
  // const setBuildingInformationToFormValues = useCallback(
  //   (area?: AreaValue, cadastralInfo?: CadastralValue, source?: SpaceSourceExternalReferenceValue) => {
  //     setValue('source', source, { shouldTouch: true, shouldValidate: true });
  //
  //     if (cadastralInfo)
  //       setValue('cadastralInformation', cadastralInfo, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
  //
  //     if (area?.metric)
  //       setValue('areaMetric', area?.metric, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
  //   },
  //   [setValue],
  // );

  useDeepCompareEffect(() => {
    reset(defaultValues);
    setPackages(defaultPackages);
    const selectedAddress = watch('address');
    setEditingAddress(!selectedAddress);
  }, [defaultValues]);

  const handleFormSubmit = useCallback(
    async (data: FormValues) => {
      await onSubmit({ ...data, packages });
    },
    [onSubmit, packages],
  );

  return (
    <Modal open={open} appear size="lg" onClose={checkCloseRequest} onBackdropClick={checkCloseRequest} title={title}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
          <ModalContent className="divide-solid divide-y min-h-[400px]">
            {/* Address - First section */}
            <article className="pb-8 px-4">
              {!isFetching ? (
                <BuildingAddressForm
                  defaultValue={selectedAddress ?? undefined}
                  onChange={setAddressToFormValues}
                  editing={editingAddress}
                  onToggleEdit={handleToggleEditAddress}
                  isEditMode={!!building}
                />
              ) : (
                <Skeleton variant="rectangular" className="h-14" />
              )}
            </article>

            {/* COMMENTED OUT: Matrikkeldetaljer - may be used in the future when NorkartSpace API is enabled */}
            {/* {selectedAddress && (
              <article className="pt-5 pb-8 px-4 border-t border-border">
                {!isFetching ? (
                  <BuildingCadastralInformationForm
                    defaultSource={selectedSource ?? undefined}
                    address={selectedAddress ?? undefined}
                    onChange={setBuildingInformationToFormValues}
                  />
                ) : (
                  <Skeleton variant="rectangular" className="h-14" />
                )}
              </article>
            )} */}

            {/* Property information - shown only after address is selected */}
            {selectedAddress && (
              <article className="pt-5 pb-8 px-4 border-t border-border space-y-4">
                <Typography variant="label" className="text-white">
                  {ready ? t('buildingCreate.propertyInformation') : <Skeleton variant="text" className="w-[40%]" />}
                </Typography>

                <GridRow cols={{ def: 1, sm: 2 }} spacingX={2} spacingY={4}>
                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="name"
                      defaultValue={''}
                      label={tGlobal('building.name')}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} data-1p-ignore />}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="type"
                      label={tGlobal('building.buildingType')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {BUILDING_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`BuildingType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="energyLabel"
                      label={tGlobal('building.energyLabel')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />

                          {ENERGY_LABEL_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="areaMetric"
                      label={tGlobal('building.area')}
                      loading={isFetching}
                      required
                      render={({ field }) => (
                        <AreaAdornment>
                          <Input type="number" className="pr-10" {...field} />
                        </AreaAdornment>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="standard"
                      label={tGlobal('space.standard')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />

                          {STANDARD_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`StandardType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                </GridRow>
              </article>
            )}

            {/* Manual cadastral details - only for manual wizard registration. The field names
                follow the PlacePoint storage convention (CadastralUnit=kommunenr, PropertyUnit=gnr,
                LeaseholdUnit=bnr — see formatMatrikkel), so the display labels are intentionally
                crossed relative to the key names. Free text so foreign identifiers (e.g. an
                Estonian katastritunnus) fit in the gnr field. */}
            {selectedAddress && showCadastralFields && (
              <article className="pt-5 pb-8 px-4 border-t border-border space-y-4">
                <Typography variant="label" className="text-white">
                  {ready ? t('buildingCreate.cadastralDetails') : <Skeleton variant="text" className="w-[40%]" />}
                </Typography>

                <GridRow cols={{ def: 2, sm: 4 }} spacingX={2} spacingY={4}>
                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="cadastralInformation.cadastralUnit"
                      label={tGlobal('building.municipalityNumber')}
                      LabelProps={{ className: 'whitespace-nowrap truncate' }}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} value={field.value ?? ''} />}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="cadastralInformation.propertyUnit"
                      label={tGlobal('building.cadastralUnit')}
                      LabelProps={{ className: 'whitespace-nowrap truncate' }}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} value={field.value ?? ''} />}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="cadastralInformation.leaseholdUnit"
                      label={tGlobal('building.propertyUnit')}
                      LabelProps={{ className: 'whitespace-nowrap truncate' }}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} value={field.value ?? ''} />}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="buildingNumber"
                      label={tGlobal('building.buildingNumber')}
                      LabelProps={{ className: 'whitespace-nowrap truncate' }}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} value={field.value ?? ''} />}
                    />
                  </GridCol>
                </GridRow>
              </article>
            )}

            {/* Tags - shown only after address is selected */}
            {selectedAddress && (
              <article className="pt-5 pb-8 px-4  border-t border-border space-y-4">
                <GridRow cols={{ def: 1, sm: 2 }} spacingX={2} spacingY={4}>
                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="tags"
                      label={tGlobal('entity.tag')}
                      loading={isFetching}
                      render={() => (
                        <TagAutocomplete defaultValue={selectedTags ?? undefined} onChange={setTagsToFormValues} />
                      )}
                    />
                  </GridCol>
                </GridRow>
              </article>
            )}

            {/* Packages - shown only after address is selected */}
            {selectedAddress && !hidePackages && (
              <BuildingPackagesSection value={packages} onChange={setPackages} />
            )}
          </ModalContent>

          <ModalFooter>
            <FormSubmitButton type="submit" disableDirtyHandling={packagesChanged} disabled={(!building && editingAddress && !selectedAddress) || isFetching || (!building && !hidePackages && packages.length === 0)}>
              {tCommon('save')}
            </FormSubmitButton>

            <Button color="primary-dark" onClick={checkCloseRequest}>
              {tCommon('close')}
            </Button>
          </ModalFooter>

          <CloseRequestModal
            open={closingRequest}
            onConfirm={handleConfirmCloseRequest}
            onCancel={handleCancelCloseRequest}
          />
        </form>
      </FormProvider>
    </Modal>
  );
}
